/**
 * LELoanOfficer - Loan officer contact information
 *
 * Displays LO name, NMLS, email, phone
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { User, Mail, Phone, BadgeCheck } from 'lucide-react';
import { LESection } from './LESection';
import type { ParsedLoanEstimate } from './types';

interface LELoanOfficerProps {
  data: ParsedLoanEstimate;
  className?: string;
}

export const LELoanOfficer: React.FC<LELoanOfficerProps> = ({
  data,
  className
}) => {
  const lo = data.loanOfficer;

  // If no LO info, don't render
  if (!lo.name && !lo.email && !lo.phone) {
    return null;
  }

  return (
    <LESection
      title="Loan Officer"
      icon={<User className="h-4 w-4" />}
      className={className}
    >
      <div className="flex items-start gap-4">
        {/* Avatar placeholder */}
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <User className="h-6 w-6 text-primary" />
        </div>

        {/* Contact Info */}
        <div className="flex-1 min-w-0">
          {/* Name */}
          <p className="font-semibold text-base">{lo.name || 'Unknown'}</p>

          {/* NMLS */}
          {lo.nmls && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
              <BadgeCheck className="h-3.5 w-3.5" />
              <span>NMLS# {lo.nmls}</span>
              {lo.licenseId && <span className="ml-2">CA# {lo.licenseId}</span>}
            </div>
          )}

          {/* Email & Phone */}
          <div className="flex flex-wrap gap-4 mt-2">
            {lo.email && (
              <a
                href={`mailto:${lo.email}`}
                className="flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <Mail className="h-3.5 w-3.5" />
                <span>{lo.email}</span>
              </a>
            )}
            {lo.phone && (
              <a
                href={`tel:${lo.phone.replace(/\D/g, '')}`}
                className="flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <Phone className="h-3.5 w-3.5" />
                <span>{lo.phone}</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Lender Info */}
      {data.lenderName && (
        <div className="mt-4 pt-3 border-t text-sm text-muted-foreground">
          <p className="font-medium text-foreground">{data.lenderName}</p>
          {data.lenderAddress && <p>{data.lenderAddress}</p>}
        </div>
      )}
    </LESection>
  );
};

export default LELoanOfficer;
